import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { MapRef } from 'react-map-gl';
import Slider from 'react-slick';
import cloudSvg from '@/assets/svg/cloud.svg';
import jobsSvg from '@/assets/svg/jobs.svg';
import kiloSvg from '@/assets/svg/kilo.svg';
import personSvg from '@/assets/svg/person.svg';
import treeSvg from '@/assets/svg/tree.svg';
import { orderCardsByCountry, orderFarmerCards } from '@/components/Cards/cardOrder';
import {
  Box,
  Card,
  Container,
  Description,
  Image,
  InfoBlock,
  SliderWrapper,
  SubText,
  Title,
} from '@/components/Cards/Cards.styles';
import {
  boundsOfOutlines,
  FOREST_OPENING_MAX_ZOOM,
  forestFitPadding,
  isMizanZoneCard,
  landingCardTitle,
  landingZoomForCard,
  treeProjectForCard,
} from '@/components/Cards/outlineBounds';
import { sliderSettings } from '@/components/Cards/utils';
import {
  getFactoriesQuery,
  getFarmerCardsQuery,
  getFarmersQuery,
  getForestsGroupQuery,
} from '@/components/Dashboard/queries';
import type { Group } from '@/components/Dashboard/types';
import { useLabels } from '@/hooks/useLabels';

interface CardsProps {
  type: string;
  setGroup: Dispatch<SetStateAction<Group | null>>;
  mapRef: RefObject<MapRef>;
  // farmerGroup + anchor (2026-09-19): that project's pins within 100 km of
  // the card's coordinates; see treeFlyTarget in outlineBounds.ts. landingZoom
  // (2026-09-21, map-ux-4): the zoom to land at - the two Mizan-landing cards
  // share the pocket but arrive at different zooms (Trees 16.25, Farm seq 16.9).
  onFlyToTrees?: (
    farmerGroup?: string,
    anchor?: [number, number] | null,
    landingZoom?: number,
  ) => void;
  // D16a - validated polygon features (planting zones + computed tree extents)
  // so a forests card can fit bounds to its project's outline. May be empty
  // (layers unpublished/failed) - the click then falls back to onFlyToTrees.
  forestOutlines?: Array<{
    geometry: { type: string; coordinates: unknown } | null;
  }>;
}

interface CardItem {
  id: string | number;
  title: string;
  type: string;
  svg_1: string;
  text_1: string | null;
  // Smaller second line under text_1 (farmer cards: the cohort caption served
  // in key_impact_data_live.labels_json as card.caption.<cohort>; null = no line).
  text_1_sub?: string | null;
  svg_2: string;
  text_2: string | null;
  text_3: string | null;
  svg_3: string;
  group: string;
  image: string;
  previewProject: string;
  extra_text: string | null;
  extra_icon: string;
  coordinates: [number, number];
  // Cohort location (from farmer_cards_public.location_label) used to find this
  // cohort's ACTUAL pins in the pin layer and fly there - the card's own centroid
  // is unreliable (some are tens/hundreds of km off the pins). null for others.
  locationLabel: string | null;
}

export const Cards = ({ type, setGroup, mapRef, onFlyToTrees, forestOutlines }: CardsProps) => {
  const { data: farmerCardsData, isLoading: isFarmersLoading } = getFarmerCardsQuery();
  const { data: forestsGroupData, isLoading: isForestsLoading } = getForestsGroupQuery();
  const { data: factoriesData, isLoading: isFactoriesLoading } = getFactoriesQuery();
  // Pin layer (same cached query useMapData uses) - lets a card fly to its
  // cohort's real pins instead of the card's own (sometimes wrong) centroid.
  const { data: farmersPins } = getFarmersQuery();

  const isLoading = isFarmersLoading || isForestsLoading || isFactoriesLoading;

  // Every word on a card comes from key_impact_data_live.labels_json via t(); the
  // fallback is the exact text production showed before PR #7 (founder
  // decision 2026-09-06: no dashboard wording lives in code).
  const { t } = useLabels();

  // Fixed cohort order: largest first, Uganda always last (founder-specified).
  const isUgandaFeature = (props: { cohort?: string; title?: string }) =>
    /uganda/i.test(props.cohort ?? '') || /uganda/i.test(props.title ?? '');
  const sortedFarmerFeatures = [...(farmerCardsData?.features ?? [])].sort((a, b) => {
    const ua = isUgandaFeature(a.properties);
    const ub = isUgandaFeature(b.properties);
    if (ua !== ub) return ua ? 1 : -1;
    return (b.properties.farmer_count ?? 0) - (a.properties.farmer_count ?? 0);
  });

  const cardsData: { type: string; data: CardItem[] }[] = [
    {
      type: 'factories',
      // Founder 2026-10-02: Ethiopian cards first, then Kenyan, the rest last;
      // served order kept within each country (see cardOrder.ts).
      data: orderCardsByCountry(
        factoriesData?.features
          ?.filter((feature) => feature.properties.title)
          .map((group) => ({
            id: group.id,
            title: group.properties.title,
            type: 'factories',
            svg_1: kiloSvg,
            text_1:
              group.properties.kg_roasted_last_year !== 'na'
                ? `${group.properties.kg_roasted_last_year} ${t(
                    'card.kg_roasted_last_year.suffix',
                    'Roasted last year',
                  )}`
                : null,
            svg_2: kiloSvg,
            text_2:
              group.properties.cups_drank !== 'na'
                ? `${group.properties.cups_drank} ${t('card.cups_drank.suffix', 'Cups drank')}`
                : null,
            group: group.properties.farmer_group,
            image: group.properties.card_image,
            previewProject: group.properties.top_project,
            text_3:
              group.properties.kilos_processed !== 'na'
                ? `${group.properties.kilos_processed} ${t('card.kilos_processed.suffix', 'Processed')}`
                : null,
            svg_3: kiloSvg,
            extra_text:
              group.properties.jobs !== 'na'
                ? `${group.properties.jobs} ${t('card.jobs.suffix', 'Jobs')}`
                : null,
            extra_icon: jobsSvg,
            coordinates: group.geometry?.coordinates ?? null,
            locationLabel: null,
          })) ?? [],
      ),
    },
    {
      type: 'farmers',
      // Cohort cards from farmer_cards_public, ordered largest-first with Uganda
      // last. flyTo targets the cohort's real pins (via locationLabel) rather than
      // the card centroid, which is unreliable for Bench Maji/Uganda.
      // Founder 2026-10-03: then a fixed list - Limu, Mizan, Kericho, Mt Elgon -
      // with every other card after it in the order above (see cardOrder.ts).
      // Superseded: the 2026-10-02 Ethiopia-then-Kenya grouping on this row.
      data: orderFarmerCards(
        sortedFarmerFeatures
          .filter((feature) => feature.properties.title)
          .map((feature) => ({
            id: feature.id ?? feature.properties.cohort,
            title: feature.properties.title,
            type: 'farmers',
            svg_1: personSvg,
            text_1: `${feature.properties.farmer_count.toLocaleString()} ${t(
              'card.farmers.unit',
              'Farmers',
            )}`,
            // Cohort caption row, keyed by the served cohort VERBATIM (the data side
            // publishes e.g. "card.caption.Bench Maji", "card.caption.Limu");
            // '' → no second line.
            text_1_sub: t(`card.caption.${feature.properties.cohort}`, '') || null,
            svg_2: treeSvg,
            text_2: null,
            text_3: null,
            svg_3: null,
            group: feature.properties.cohort,
            image: feature.properties.card_image ?? '',
            previewProject: 'yes',
            extra_text: null,
            extra_icon: null,
            coordinates: feature.geometry?.coordinates ?? null,
            locationLabel: feature.properties.location_label ?? null,
          })),
      ),
    },
    {
      type: 'forests',
      data:
        forestsGroupData?.features
          ?.filter((feature) => feature.properties.title)
          .map((group) => ({
            id: group.id,
            title: group.properties.title,
            type: 'forests',
            svg_1: cloudSvg,
            text_1:
              group.properties.ton_co2_sequestered !== 'na'
                ? `${group.properties.ton_co2_sequestered} ${t(
                    'card.ton_co2_sequestered.suffix',
                    'Mton CO2 sequestered',
                  )}`
                : null,
            svg_2: treeSvg,
            text_2:
              group.properties.trees_planted !== 'na'
                ? `${group.properties.trees_planted} ${t('card.trees_planted.suffix', 'Trees planted')}`
                : null,
            text_3:
              group.properties.ha_forest_protected !== 'na'
                ? `${group.properties.ha_forest_protected} ${t(
                    'card.ha_forest_protected.suffix',
                    'Forest protected',
                  )}`
                : null,
            svg_3: treeSvg,
            group: group.properties.title,
            image: group.properties.card_image,
            previewProject: group.properties.top_project,
            extra_text:
              group.properties.kg_biocompost_used !== 'na'
                ? `Biocompost made: ${group.properties.kg_biocompost_used}`
                : null,
            extra_icon: treeSvg,
            coordinates: group.geometry?.coordinates ?? null,
            locationLabel: null,
          })) ?? [],
    },
  ];

  const filteredData = type
    ? cardsData.filter((card) => card.type === type)
    : cardsData.map((card) => ({
        type: card.type,
        data: card.data.filter((c) => c.previewProject === 'yes'),
      }));

  // Bounding box of a cohort's actual pins, matched on location (pin.location vs
  // card.location_label - exact for most cohorts, prefix-tolerant for Bench Maji).
  // Returns null if no pins match, so the caller can fall back to the centroid.
  const boundsForLocation = (
    locationLabel: string | null,
  ): [[number, number], [number, number]] | null => {
    const target = (locationLabel ?? '').trim().toLowerCase();
    if (!target || !farmersPins?.features?.length) return null;
    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    for (const f of farmersPins.features) {
      const loc = String(f.properties?.location ?? '')
        .trim()
        .toLowerCase();
      const coords = f.geometry?.coordinates;
      if (!coords || !loc) continue;
      if (loc !== target && !loc.startsWith(target) && !target.startsWith(loc)) continue;
      const [lng, lat] = coords as [number, number];
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    }
    if (minLng === Infinity) return null;
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ];
  };

  // The pins' farmer_group for a cohort, found via the card's location (the pin
  // layer's farmer_group is authoritative - farmer_group_summary disagrees, e.g.
  // it says "Mizan Farmers" while the pins say "Mizan Outgrower").
  const farmerGroupForLocation = (locationLabel: string | null): string | undefined => {
    const target = (locationLabel ?? '').trim().toLowerCase();
    if (!target || !farmersPins?.features?.length) return undefined;
    const match = farmersPins.features.find((f) => {
      const loc = String(f.properties?.location ?? '')
        .trim()
        .toLowerCase();
      return loc && (loc === target || loc.startsWith(target) || target.startsWith(loc));
    });
    return match?.properties?.farmer_group ?? undefined;
  };

  const handleCardClick = (item: CardItem) => {
    // Open the project view first, and carry the cohort's farmer_group so the
    // farmer list can filter to it. setGroup runs regardless of map readiness so
    // a click always does something.
    const farmerGroup =
      item.type === 'farmers' ? farmerGroupForLocation(item.locationLabel) : undefined;
    setGroup({ type: item.type, group: item.title, farmerGroup });

    const map = mapRef.current?.getMap();
    if (!map) return;

    if (item.type === 'farmers') {
      // Fly to the cohort's real pins; the card centroid can be far off.
      const bounds = boundsForLocation(item.locationLabel);
      if (bounds) {
        map.fitBounds(bounds, { padding: 80, maxZoom: 13, duration: 1500 });
      } else if (item.coordinates) {
        map.flyTo({ center: item.coordinates, zoom: 11, speed: 1.5, essential: true });
      }
      return;
    }

    if (item.type === 'forests') {
      // D16a: fit bounds to this project's outline. No shared key links a
      // forests card to an outline layer (cards key on title; outlines carry
      // farmer_group or nothing), so the match is geometric - outlines whose
      // bbox contains the card's own centroid. Data-derived, not a guessed
      // label mapping (§3). No match -> the pre-existing fly-to-trees fallback.
      //
      // Restored 2026-09-17 (founder: the 2026-09-17 "fit every card to Mizan"
      // change over-applied - only the card whose own centroid falls inside
      // the Mizan outline should get this fit; the others, and the FORESTS
      // tab's own activation, keep the original unfiltered onFlyToTrees).
      //
      // Superseded 2026-09-18 (founder decision): the Mizan zone + extent fit
      // belongs to the "100% Deforestation free" card, whose row carries NULL
      // geometry, so it is matched by its stable title (see isMizanZoneCard).
      // The other three cards - INCLUDING "Farm sequestration in Mizan", whose
      // centroid does fall inside the outline - fly to the tree pins like
      // before. The geometric match is therefore taken out of the click path
      // but kept in place (§2) for a future card that carries coordinates
      // inside an outline of its own:
      //   const bounds = boundsOfOutlinesContaining(item.coordinates, forestOutlines);
      const bounds = isMizanZoneCard(item.title) ? boundsOfOutlines(forestOutlines) : null;
      if (bounds) {
        map.fitBounds(bounds, {
          padding: forestFitPadding(window.innerWidth),
          maxZoom: FOREST_OPENING_MAX_ZOOM,
          duration: 1500,
        });
      } else {
        // Founder 2026-09-18/19: fly to THIS card's project pins within 100 km
        // of the card's own coordinates; none -> the card itself at zoom 12.
        // Founder 2026-09-20: "Trees, Ethiopia" borrows the landing of "Farm
        // sequestration in Mizan" (LANDING_CARD_ALIAS) - its project AND its
        // anchor - so both cards land on the same Mizan tree area. If the
        // aliased card is not served, the card falls back to its own landing.
        const landingTitle = landingCardTitle(item.title);
        const landing =
          cardsData
            .find((c) => c.type === 'forests')
            ?.data.find((c) => (c.title ?? '').trim().toLowerCase() === landingTitle) ?? item;
        // The landing zoom is keyed by the card the visitor CLICKED (item.title),
        // not the aliased landing card, so "Trees, Ethiopia" lands at 16.25 (dots)
        // while "Farm sequestration" lands at 16.9 (pins) on the same pocket.
        onFlyToTrees?.(
          treeProjectForCard(landing.title),
          landing.coordinates ?? null,
          landingZoomForCard(item.title),
        );
      }
      return;
    }

    // factories
    if (item.coordinates) {
      map.flyTo({ center: item.coordinates, zoom: 12, speed: 1.5, essential: true });
    }
  };

  if (isLoading) {
    return <p>Loading</p>;
  }

  return (
    <Container>
      <Box>
        <SliderWrapper>
          <Slider {...sliderSettings}>
            {filteredData.map((category) =>
              category.data.map((item) => (
                <Card key={item.id} type={item.type} onClick={() => handleCardClick(item)}>
                  <Image src={item.image} />
                  <InfoBlock>
                    <Title>{item.title}</Title>
                    {item.text_1 && (
                      <Description>
                        <img src={item.svg_1} />
                        <p>
                          {item.text_1}
                          {item.text_1_sub && <SubText>{item.text_1_sub}</SubText>}
                        </p>
                      </Description>
                    )}
                    {item.text_2 && (
                      <Description>
                        <img src={item.svg_2} />
                        <p>{item.text_2}</p>
                      </Description>
                    )}
                    {item.text_3 && (
                      <Description>
                        <img src={item.svg_3} />
                        <p>{item.text_3}</p>
                      </Description>
                    )}
                    {item.extra_text && (
                      <Description key={item.extra_text}>
                        <img src={item.extra_icon} />
                        <p>{item.extra_text}</p>
                      </Description>
                    )}
                  </InfoBlock>
                </Card>
              )),
            )}
          </Slider>
        </SliderWrapper>
      </Box>
    </Container>
  );
};
